import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Upload, FileIcon, X } from 'lucide-react';
import { EntityLinkSelector, STANDARD_LINKABLE_TYPES } from '@/components/shared/EntityLinkSelector';
import { UserSelector } from '@/components/shared/UserSelector';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { formatFileSize } from '@/utils/formatFileSize';
import type { EntityType } from '@/types/link';

interface SelectedEntity {
  id: string;
  type: EntityType;
  name: string;
}

interface EvidenceCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (data: any, links?: SelectedEntity[]) => void;
  preLinkedEntity?: SelectedEntity;
}

// Exclude 'evidence' from allowed types (can't link evidence to itself)
const ALLOWED_LINK_TYPES = STANDARD_LINKABLE_TYPES.filter(t => t !== 'evidence');

export function EvidenceCreateDialog({ open, onOpenChange, onSave, preLinkedEntity }: EvidenceCreateDialogProps) {
  const [formData, setFormData] = useState({
    name: '',
    type: 'document',
    notes: '',
    owner_id: undefined as string | undefined,
    storage_path: undefined as string | undefined,
    file_size: undefined as number | undefined,
  });
  const [selectedLinks, setSelectedLinks] = useState<SelectedEntity[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  // Pre-populate links when entity is provided
  useEffect(() => {
    if (preLinkedEntity && open) {
      setSelectedLinks([preLinkedEntity]);
    } else if (!open) {
      setSelectedLinks([]);
    }
  }, [preLinkedEntity, open]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      // Auto-populate name from filename if empty
      if (!formData.name) {
        setFormData({ ...formData, name: selectedFile.name.replace(/\.[^/.]+$/, ''), type: selectedFile.type || 'document' });
      }
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error('Please provide a name');
      return;
    }

    setUploading(true);

    try {
      let evidenceData = { ...formData };

      // If file is selected, upload it first
      if (file) {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');

        const { data: profile } = await supabase
          .from('profiles')
          .select('org_id')
          .eq('user_id', user.id)
          .single();

        if (!profile?.org_id) throw new Error('No organization found');

        const timestamp = Date.now();
        const fileName = `${timestamp}_${file.name}`;
        const filePath = `${profile.org_id}/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('evidence')
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: false,
          });

        if (uploadError) throw uploadError;

        evidenceData = {
          ...evidenceData,
          storage_path: filePath,
          file_size: file.size,
        };
      }

      onSave(evidenceData, selectedLinks);
      
      // Reset form
      setFormData({ name: '', type: 'document', notes: '', owner_id: undefined, storage_path: undefined, file_size: undefined });
      setSelectedLinks([]);
      setFile(null);
    } catch (error) {
      console.error('Error creating evidence:', error);
      toast.error('Failed to create evidence');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Evidence</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="file">Attach File (Optional)</Label>
            {!file ? (
              <div className="border-2 border-dashed rounded-lg p-6 text-center hover:border-primary/50 transition-colors">
                <input
                  id="file"
                  type="file"
                  onChange={handleFileChange}
                  className="hidden"
                  disabled={uploading}
                />
                <label htmlFor="file" className="cursor-pointer">
                  <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    Click to upload or drag and drop
                  </p>
                </label>
              </div>
            ) : (
              <div className="border rounded-lg p-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileIcon className="w-8 h-8 text-primary" />
                  <div>
                    <p className="font-medium text-sm">{file.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatFileSize(file.size)}
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleRemoveFile}
                  disabled={uploading}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
          <div>
            <Label htmlFor="name">Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Evidence name"
              disabled={uploading}
            />
          </div>
          <div>
            <Label htmlFor="type">Type</Label>
            <Input
              id="type"
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              placeholder="Document type"
              disabled={uploading}
            />
          </div>
          <div>
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Additional notes"
              disabled={uploading}
            />
          </div>

          <UserSelector
            value={formData.owner_id}
            onChange={(owner_id) => setFormData({ ...formData, owner_id })}
            label="Owner"
            placeholder="Select owner (optional)"
            allowUnassigned
          />

          <EntityLinkSelector
            allowedTypes={ALLOWED_LINK_TYPES}
            onLinksChange={setSelectedLinks}
            initialLinks={selectedLinks}
          />
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={uploading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!formData.name || uploading}>
            <Plus className="w-4 h-4 mr-2" />
            {uploading ? 'Creating...' : 'Add Evidence'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
